import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Sun,
  Moon,
  Search,
  Sparkles,
  Bell,
  LogOut,
  User,
  ExternalLink,
  Shield,
  Menu,
} from 'lucide-react';

export default function Header({ collapsed, onToggleAiDrawer, onMobileMenuToggle }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`sticky top-0 z-30 flex items-center justify-between h-16 px-4 sm:px-6 border-b transition-all ${
        theme === 'dark'
          ? 'bg-slate-950/80 backdrop-blur-md border-slate-800 text-slate-100'
          : 'bg-white/80 backdrop-blur-md border-slate-200 text-slate-900'
      } ${collapsed ? 'lg:ml-20' : 'lg:ml-64'}`}
    >
      {/* Left side: hamburger + search */}
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        {/* Mobile hamburger */}
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors flex-shrink-0"
          title="Open menu"
        >
          <Menu size={20} />
        </button>

        {/* Search — hidden on very small screens, visible from sm breakpoint */}
        <div className="relative hidden sm:block w-full max-w-sm lg:max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search pages, services, posts..."
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-slate-800 bg-slate-900/60 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
          />
        </div>
      </div>

      {/* Right controls */}
      <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
        {/* View Public Website — hide label on mobile */}
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="hidden sm:flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
        >
          <span>Live Site</span>
          <ExternalLink size={14} />
        </a>

        {/* AI Assistant Quick Trigger — hide label on mobile */}
        <button
          onClick={onToggleAiDrawer}
          className="flex items-center space-x-1.5 px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-md shadow-purple-600/20 transition-all transform active:scale-95"
        >
          <Sparkles size={14} className="animate-pulse" />
          <span className="hidden sm:inline">AI Copilot</span>
        </button>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
          title="Toggle Theme"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications Icon */}
        <button className="relative p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-slate-950" />
        </button>

        {/* User Menu Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center space-x-3 p-1 rounded-xl hover:bg-slate-800 transition-colors"
          >
            <img
              src={user?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200'}
              alt={user?.name}
              className="w-8 h-8 rounded-lg object-cover ring-2 ring-blue-500/40"
            />
            <div className="hidden md:flex flex-col text-left">
              <span className="text-xs font-semibold text-slate-200">{user?.name || 'Admin User'}</span>
              <span className="text-[10px] text-blue-400 font-medium">{user?.role || 'SUPER_ADMIN'}</span>
            </div>
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl p-2 z-50 text-slate-200 animate-in fade-in slide-in-from-top-2 duration-150">
              <div className="px-3 py-2 border-b border-slate-800">
                <p className="text-xs font-bold text-white">{user?.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
              </div>
              <div className="py-1">
                <a
                  href="/admin/settings"
                  className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs hover:bg-slate-800 transition-colors"
                >
                  <User size={14} />
                  <span>Profile Settings</span>
                </a>
                <a
                  href="/admin/users"
                  className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs hover:bg-slate-800 transition-colors"
                >
                  <Shield size={14} />
                  <span>Roles & Permissions</span>
                </a>
              </div>
              <div className="pt-1 border-t border-slate-800">
                <button
                  onClick={logout}
                  className="w-full flex items-center space-x-2 px-3 py-2 rounded-xl text-xs text-rose-400 hover:bg-rose-500/10 transition-colors text-left"
                >
                  <LogOut size={14} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
